import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

interface PieData {
  name: string;
  value: number;
  fill: string;
}

interface StatCardWithPieProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  pieData: PieData[];
  height?: number;
  onClick?: () => void;
  tooltip?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CardContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%);
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(76, 110, 245, 0.08);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 18px 40px rgba(76, 110, 245, 0.15);
    border-color: rgba(76, 110, 245, 0.4);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const IconWrapper = styled.div`
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  svg {
    width: 28px;
    height: 28px;
  }
  span {
    font-size: 28px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Title = styled.p`
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
`;

const Value = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  text-align: left;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 100px;
  margin-top: auto;
`;

const TooltipOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(30, 27, 75, 0.92);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.1rem;
  z-index: 10;
  animation: ${fadeIn} 0.2s ease-out;
`;

const TooltipText = styled.p`
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.72rem;
  line-height: 1.55;
  margin: 0;
  text-align: center;
  font-weight: 400;
  letter-spacing: 0.1px;
`;

const useAnimatedCounter = (targetValue: number, duration: number = 2000) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValue = useRef(0);
  const animationRef = useRef<number>();

  useEffect(() => {
    const startValue = previousValue.current;
    const difference = targetValue - startValue;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(startValue + difference * easeOut);
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValue.current = targetValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [targetValue, duration]);

  return displayValue;
};

const StatCardWithPie: React.FC<StatCardWithPieProps> = ({ icon, title, value, pieData, height, onClick, tooltip }) => {
  const numericValue = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  const animatedValue = useAnimatedCounter(numericValue, 2000);
  const [showTooltip, setShowTooltip] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (!tooltip) return;
    hoverTimerRef.current = setTimeout(() => setShowTooltip(true), 600);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setShowTooltip(false);
  };

  return (
    <CardContainer
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence>
        {showTooltip && tooltip && (
          <TooltipOverlay>
            <TooltipText>{tooltip}</TooltipText>
          </TooltipOverlay>
        )}
      </AnimatePresence>

      <Header>
        <IconWrapper>{icon}</IconWrapper>
        <Title>{title}</Title>
      </Header>
      <Value>{animatedValue}</Value>
      <ChartWrapper style={{ height: height || 100 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={20}
              outerRadius={40}
              fill="#8884d8"
              paddingAngle={pieData.length > 1 ? 5 : 0}
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '8px',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              itemStyle={{ color: '#2d3748' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </CardContainer>
  );
};

export default StatCardWithPie;
